/**
 * Lead Edit Form Component
 * =========================
 * Form for editing existing lead information with change tracking and research regeneration
 */

import React, { useState, useEffect, useRef } from 'react';
import { leadAPI, proposalAPI } from '../../api';
import { Button, Input, Modal } from '../ui';

const LeadEditForm = ({ lead, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: lead.name || '',
    designation: lead.designation || '',
    company: lead.company || '',
    email: lead.email || '',
    phone: lead.phone || '',
    website: lead.website || '',
    linkedin: lead.linkedin || '',
    notes: lead.notes || '',
    lead_source: lead.lead_source || '',
    tier: lead.tier || 'tier_5',
    avatar_config_id: lead.avatar_config_id || '',
  });
  const [originalData, setOriginalData] = useState({});
  const [avatarConfigs, setAvatarConfigs] = useState([]);
  const [changedFields, setChangedFields] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const errorRef = useRef(null);

  // Store original data on mount
  useEffect(() => {
    const original = {
      name: lead.name || '',
      designation: lead.designation || '',
      company: lead.company || '',
      email: lead.email || '',
      phone: lead.phone || '',
      website: lead.website || '',
      linkedin: lead.linkedin || '',
      notes: lead.notes || '',
      lead_source: lead.lead_source || '',
      tier: lead.tier || 'tier_5',
      avatar_config_id: lead.avatar_config_id || '',
    };
    setOriginalData(original);
  }, [lead]);

  // Track changed fields
  useEffect(() => {
    const changed = Object.keys(formData).filter(
      (key) => formData[key] !== originalData[key]
    );
    setChangedFields(changed);
  }, [formData, originalData]);

  // Scroll to error message when it appears
  useEffect(() => {
    if (errors.submit && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errors.submit]);

  useEffect(() => {
    proposalAPI.getAvatarConfigs().then(res => {
      if (res.data) setAvatarConfigs(res.data.filter(c => c.is_active));
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    // Phone is now optional - no validation required

    // Email validation (optional but must be valid if provided)
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // URL validation for website
    if (formData.website.trim() && !formData.website.startsWith('http://') && !formData.website.startsWith('https://')) {
      newErrors.website = 'Website must start with http:// or https://';
    }

    // URL validation for LinkedIn
    if (formData.linkedin.trim() && !formData.linkedin.startsWith('http://') && !formData.linkedin.startsWith('https://')) {
      newErrors.linkedin = 'LinkedIn URL must start with http:// or https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    // Check if any fields changed
    if (changedFields.length === 0) {
      setErrors({ submit: 'No changes detected' });
      return;
    }
    
    // Show confirmation modal
    setShowConfirmation(true);
  };

  const handleConfirmUpdate = async () => {
    setLoading(true);
    setShowConfirmation(false);
    
    try {
      // Only send changed fields
      const updateData = changedFields.reduce((acc, field) => {
        acc[field] = formData[field];
        return acc;
      }, {});

      if ('avatar_config_id' in updateData && updateData.avatar_config_id === '') {
        updateData.avatar_config_id = null;
      }

      // `trigger_research_refresh` is deliberately not sent. It defaults to
      // false on the backend, and no portal screen asks for a research run.
      // Lead research has been dormant since 2026-09-07 — see the cycle
      // `diligence-only-lead-tab-and-branded-report`.
      
      const response = await leadAPI.updateLead(lead.id, updateData);
      
      if (response.data) {
        onSuccess && onSuccess(response.data);
      }
    } catch (err) {
      if (err.code === 'VALIDATION_ERROR' && err.details && Object.keys(err.details).length > 0) {
        setErrors({ ...err.details, submit: err.message || 'Please fix the validation errors below' });
      } else {
        setErrors({ submit: err.message || 'Failed to update lead' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  const isFieldChanged = (fieldName) => {
    return changedFields.includes(fieldName);
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Error Message */}
        {errors.submit && (
          <div ref={errorRef} className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}

        {/* Changed Fields Indicator */}
        {changedFields.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">
            <p className="font-medium">Fields changed: {changedFields.length}</p>
            <p className="text-sm mt-1">{changedFields.join(', ')}</p>
          </div>
        )}

        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name <span className="text-red-500">*</span>
              {isFieldChanged('name') && <span className="ml-2 text-blue-600 text-xs">●</span>}
            </label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Smith"
              error={errors.name}
              className={isFieldChanged('name') ? 'border-blue-400' : ''}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Designation
              {isFieldChanged('designation') && <span className="ml-2 text-blue-600 text-xs">●</span>}
            </label>
            <Input
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              placeholder="Chief Investment Officer"
              error={errors.designation}
              className={isFieldChanged('designation') ? 'border-blue-400' : ''}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company <span className="text-red-500">*</span>
              {isFieldChanged('company') && <span className="ml-2 text-blue-600 text-xs">●</span>}
            </label>
            <Input
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Global Asset Management LLC"
              error={errors.company}
              className={isFieldChanged('company') ? 'border-blue-400' : ''}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
              {isFieldChanged('email') && <span className="ml-2 text-blue-600 text-xs">●</span>}
            </label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john.smith@company.com"
              error={errors.email}
              className={isFieldChanged('email') ? 'border-blue-400' : ''}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone
              {isFieldChanged('phone') && <span className="ml-2 text-blue-600 text-xs">●</span>}
            </label>
            <Input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1-555-0123"
              error={errors.phone}
              className={isFieldChanged('phone') ? 'border-blue-400' : ''}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website
              {isFieldChanged('website') && <span className="ml-2 text-blue-600 text-xs">●</span>}
            </label>
            <Input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="https://company.com"
              error={errors.website}
              className={isFieldChanged('website') ? 'border-blue-400' : ''}
            />
          </div>
        </div>

        {/* Additional Information */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              LinkedIn Profile
              {isFieldChanged('linkedin') && <span className="ml-2 text-blue-600 text-xs">●</span>}
            </label>
            <Input
              type="url"
              name="linkedin"
              value={formData.linkedin}
              onChange={handleChange}
              placeholder="https://linkedin.com/in/johnsmith"
              error={errors.linkedin}
              className={isFieldChanged('linkedin') ? 'border-blue-400' : ''}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lead Source
              {isFieldChanged('lead_source') && <span className="ml-2 text-blue-600 text-xs">●</span>}
            </label>
            <Input
              name="lead_source"
              value={formData.lead_source}
              onChange={handleChange}
              placeholder="Conference - Dubai Fintech Summit 2026"
              error={errors.lead_source}
              className={isFieldChanged('lead_source') ? 'border-blue-400' : ''}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lead Tier
              {isFieldChanged('tier') && <span className="ml-2 text-blue-600 text-xs">●</span>}
            </label>
            <select
              name="tier"
              value={formData.tier}
              onChange={handleChange}
              className={`input bg-white ${isFieldChanged('tier') ? 'border-blue-400' : ''}`}
            >
              <option value="tier_1">Tier 1 - Critical</option>
              <option value="tier_2">Tier 2 - High</option>
              <option value="tier_3">Tier 3 - Medium</option>
              <option value="tier_4">Tier 4 - Low</option>
              <option value="tier_5">Tier 5 - Default</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Presenter
              {isFieldChanged('avatar_config_id') && <span className="ml-2 text-blue-600 text-xs">●</span>}
            </label>
            <select
              name="avatar_config_id"
              value={formData.avatar_config_id}
              onChange={handleChange}
              className={`input bg-white ${isFieldChanged('avatar_config_id') ? 'border-blue-400' : ''}`}
            >
              <option value="">Select presenter</option>
              {avatarConfigs.map(config => (
                <option key={config.id} value={config.id}>{config.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
              {isFieldChanged('notes') && <span className="ml-2 text-blue-600 text-xs">●</span>}
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder="Additional notes about this lead..."
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0 focus:border-primary ${
                isFieldChanged('notes') ? 'border-blue-400' : 'border-gray-300'
              }`}
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading || changedFields.length === 0}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmation}
        onClose={handleCancelConfirmation}
        title="Confirm Lead Update"
        size="md"
        closeOnOverlayClick={!loading}
        showCloseButton={!loading}
        footer={
          <>
            <Button
              variant="outline"
              onClick={handleCancelConfirmation}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmUpdate}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Confirm Update'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            You are about to update the following fields:
          </p>
          <ul className="list-disc list-inside space-y-1 text-gray-700">
            {changedFields.map((field) => (
              <li key={field}>
                <span className="font-medium capitalize">{field.replace('_', ' ')}</span>
                {': '}
                <span className="text-gray-500">{originalData[field] || '(empty)'}</span>
                {' → '}
                <span className="text-primary font-medium">{formData[field] || '(empty)'}</span>
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    </>
  );
};

export default LeadEditForm;


